import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined'
import { Box, Button, Divider, InputBase, Stack, useMediaQuery, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import useStyles, { textFreeQuestionSx } from './_styles'
import { TextFreeQuestionProps } from './_props'
import ErrorIcon from '@mui/icons-material/Error'

export default function TextFreeQuestion({
    question,
    initialValue,
    onAnswer,
    showInitialError,
}: TextFreeQuestionProps) {
    const classes = useStyles()
    const [value, setValue] = useState<string>(initialValue ?? '')
    const [history, setHistory] = useState<string[]>([initialValue ?? ''])
    const [historyIndex, setHistoryIndex] = useState<number>(0)
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
    const isUndoRedoInProgress = useRef(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const lastChangeTimeRef = useRef<number>(0)
    const isShortScreen = useMediaQuery('(max-height:700px)')
    const isMediumScreen = useMediaQuery('(max-height:900px)')
    const maxRows = isShortScreen ? 7 : isMediumScreen ? 11 : 16

    useEffect(() => {
        const newValue = initialValue ?? ''
        setValue(newValue)
        setHistory([newValue])
        setHistoryIndex(0)
        setErrorMessage(showInitialError ? getErrorMessage(newValue) : undefined)
        isUndoRedoInProgress.current = false
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [question?.id])

    const getErrorMessage = (newValue: string) =>
        question?.required && newValue.trim().length === 0 ? 'Une saisie est requise' : undefined

    const addToHistory = (newValue: string) => {
        if (isUndoRedoInProgress.current) return

        const currentHistoryValue = history[historyIndex]
        if (newValue === currentHistoryValue) return

        const now = Date.now()
        const timeSinceLastChange = now - lastChangeTimeRef.current
        if (timeSinceLastChange < 300 && historyIndex === history.length - 1) {
            const newHistory = [...history]
            newHistory[historyIndex] = newValue
            setHistory(newHistory)
        } else {
            const newHistory = history.slice(0, historyIndex + 1)
            newHistory.push(newValue)
            if (newHistory.length > 200) {
                newHistory.shift()
                setHistoryIndex((prev) => prev - 1)
            }
            setHistory(newHistory)
            setHistoryIndex(newHistory.length - 1)
        }
        lastChangeTimeRef.current = now
    }

    const handleChange = (newValue: string) => {
        setValue(newValue)
        setErrorMessage(getErrorMessage(newValue))
        addToHistory(newValue)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
            onAnswer?.(newValue)
        }, 500)
    }

    const handleUndo = () => {
        if (historyIndex > 0) {
            isUndoRedoInProgress.current = true
            const newIndex = historyIndex - 1
            const previousValue = history[newIndex]
            setValue(previousValue)
            setErrorMessage(getErrorMessage(previousValue))
            setHistoryIndex(newIndex)
            onAnswer?.(previousValue)
            setTimeout(() => {
                isUndoRedoInProgress.current = false
            }, 0)
        }
    }

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            isUndoRedoInProgress.current = true
            const newIndex = historyIndex + 1
            const nextValue = history[newIndex]
            setValue(nextValue)
            setErrorMessage(getErrorMessage(nextValue))
            setHistoryIndex(newIndex)
            onAnswer?.(nextValue)
            setTimeout(() => {
                isUndoRedoInProgress.current = false
            }, 0)
        }
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement
            if (!target.closest('textarea')) return
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                e.preventDefault()
                if (e.shiftKey) {
                    handleRedo()
                } else {
                    handleUndo()
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
                e.preventDefault()
                handleRedo()
            }
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [historyIndex, history])

    return (
        <Stack sx={[textFreeQuestionSx.editor, errorMessage ? textFreeQuestionSx.textFreeQuestionError : {}]}>
            <Box
                sx={{
                    width: '100%',
                    padding: {
                        xs: 0,
                        lg: '0 1.5rem',
                    },
                }}
            >
                <Box sx={textFreeQuestionSx.toolbar}>
                    <Button
                        disableRipple
                        startIcon={<UndoOutlinedIcon />}
                        onClick={handleUndo}
                        disabled={historyIndex === 0}
                        className={classes.toolbarButton}
                    >
                        Annuler
                    </Button>
                    <Button
                        disableRipple
                        endIcon={<RedoOutlinedIcon />}
                        onClick={handleRedo}
                        disabled={historyIndex === history.length - 1}
                        className={classes.toolbarButton}
                    >
                        Rétablir
                    </Button>
                </Box>
                <Divider sx={textFreeQuestionSx.customDivider} />
                <InputBase
                    multiline
                    fullWidth
                    maxRows={maxRows}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="Saisir ou copier-coller le texte ici"
                    sx={textFreeQuestionSx.textarea}
                />
            </Box>
            {errorMessage && (
                <Stack sx={[textFreeQuestionSx.textFreeQuestionErrorMessage, { borderWidth: 2 }]}>
                    <ErrorIcon sx={textFreeQuestionSx.errorIcon} />
                    <Typography>{errorMessage}</Typography>
                </Stack>
            )}
        </Stack>
    )
}
